/**
 * order-action-confirm.ts — confirmation copy for seller forward order actions.
 *
 * Maps a forward target status to the i18n keys used by the ConfirmDialog shown
 * before the action runs (order list quick actions + order detail primary).
 * Review/redirect statuses (payment proof review) never reach here — they open
 * the detail instead of mutating.
 */

import type { OrderStatus } from "@/lib/api/types";
import { keys } from "@/lib/i18n/portal";

export type OrderActionConfirmCopy = {
  titleKey: string;
  bodyKey: string;
  confirmKey: string;
};

const CONFIRM_COPY: Partial<Record<OrderStatus, OrderActionConfirmCopy>> = {
  confirmed: {
    titleKey: keys.orders.quickConfirmTitleConfirmed,
    bodyKey: keys.orders.quickConfirmBodyConfirmed,
    confirmKey: keys.orders.metaNextConfirmOrder,
  },
  preparing: {
    titleKey: keys.orders.quickConfirmTitlePreparing,
    bodyKey: keys.orders.quickConfirmBodyPreparing,
    confirmKey: keys.orders.metaNextStartPreparing,
  },
  shipped: {
    titleKey: keys.orders.quickConfirmTitleShipped,
    bodyKey: keys.orders.quickConfirmBodyShipped,
    confirmKey: keys.orders.metaNextMarkShipped,
  },
  delivered: {
    titleKey: keys.orders.quickConfirmTitleDelivered,
    bodyKey: keys.orders.quickConfirmBodyDelivered,
    confirmKey: keys.orders.metaNextMarkDelivered,
  },
};

/** Confirmation copy for a forward target, falling back to the confirm copy. */
export function getOrderActionConfirmCopy(
  target: OrderStatus,
): OrderActionConfirmCopy {
  return CONFIRM_COPY[target] ?? CONFIRM_COPY.confirmed!;
}
